import { ParsedQuery } from "query-string";
import {
    cleanSearchParams as cleanSharedSearchParams,
    getSingleQueryValue,
    joinCsv,
    normalizeSearchParams,
    type PagerParams,
    type SearchParamValue,
    splitCsv,
} from "components/ListPage/filters/searchParamsHelpers";

export type OurAdListParams = HashMap<SearchParamValue> & {
    filter_keyword?: string;
    filter_categories?: string;
    filter_subcategories?: string;
    filter_frequencies?: string;
    filter_posted_period?: string;
    filter_city?: string;
    filter_range?: string | number;
    filter_coordinates_lat?: string | number;
    filter_coordinates_lon?: string | number;
    per_page?: number;
    page?: number;
    order?: string;
    direction?: string;
};

type BuildSearchParamsOptions = {
    activeFilters: HashMap<unknown>;
    keyword?: string;
    pager: PagerParams;
};

type ResetInlineAdFilterSearchParamsOptions = {
    currentQueryValues: ParsedQuery;
    pager: PagerParams;
};

const INLINE_AD_FILTER_KEYS = [
    "filter_categories",
    "filter_subcategories",
    "filter_frequencies",
    "filter_posted_period",
    "filter_city",
    "filter_range",
    "filter_coordinates_lat",
    "filter_coordinates_lon",
] as const;

export { getSingleQueryValue, joinCsv, splitCsv };

export const cleanSearchParams = (params: OurAdListParams): OurAdListParams =>
    cleanSharedSearchParams(params, {
        shouldKeep: (key, value) => key !== "filter_frequencies" || splitCsv(value).length === 1,
    });

export const buildSearchParams = ({
    activeFilters,
    keyword,
    pager,
}: BuildSearchParamsOptions): OurAdListParams =>
    cleanSearchParams({
        ...normalizeSearchParams<OurAdListParams>(activeFilters),
        filter_keyword: keyword?.trim() || undefined,
        per_page: pager.per_page,
        page: 1,
        order: pager.order,
        direction: pager.direction,
    });

export const buildResetInlineAdFilterSearchParams = ({
    currentQueryValues,
    pager,
}: ResetInlineAdFilterSearchParamsOptions): OurAdListParams =>
    cleanSearchParams({
        filter_keyword: getSingleQueryValue(currentQueryValues.filter_keyword).trim() || undefined,
        per_page: pager.per_page,
        page: 1,
        order: pager.order,
        direction: pager.direction,
    });

export const hasActiveInlineAdFilters = (params: HashMap<unknown>): boolean =>
    INLINE_AD_FILTER_KEYS.some((key) => {
        const value = params[key];

        if (key === "filter_range") {
            return value !== undefined && value !== null && value !== "";
        }

        if (key === "filter_coordinates_lat" || key === "filter_coordinates_lon") {
            return false;
        }

        if (key === "filter_frequencies") {
            return splitCsv(value).length === 1;
        }

        if (key === "filter_categories" || key === "filter_subcategories") {
            return splitCsv(value).length > 0;
        }

        return value !== undefined && value !== null && value !== "";
    });
